import { useEffect, useState } from "react";
//--------------------------------------------------------------------------------------------
import Modal from "src/components/common/modal";
import { myAxios, MyFormData } from "src/components/common/functions";
import { formatQty, isPositiveQuantityText, userEmailFromText, userLabel, userOptionLabel } from "./inventoryUtils";
//--------------------------------------------------------------------------------------------
export default function MoveInventoryModal({ closeIt, onDone, yards, stock, users, yardMap, itemMap, setShowLoader, initialStockRow = null, initialDestinationType = "yard", lockDestinationType = false, title = "Move Inventory" }){
    const yardsWithStock = yards.filter(yard => stock.some(row => String(row.yardId) === String(yard.id) && Number(row.quantity) > 0));
    const [sourceYardId, setSourceYardId] = useState(initialStockRow?.yardId || yardsWithStock[0]?.id || "");
    const availableRows = stock.filter(row => String(row.yardId) === String(sourceYardId) && Number(row.quantity) > 0);
    const [itemId, setItemId] = useState(initialStockRow?.itemId || availableRows[0]?.itemId || "");
    const selectedStock = stock.find(row => String(row.yardId) === String(sourceYardId) && String(row.itemId) === String(itemId));
    const [quantity, setQuantity] = useState("");
    const [runnerText, setRunnerText] = useState("");
    const [destinationType, setDestinationType] = useState(initialDestinationType);
    const [destinationYardId, setDestinationYardId] = useState(() => (initialDestinationType === "yard" ? (yards.find(yard => String(yard.id) !== String(initialStockRow?.yardId || yardsWithStock[0]?.id || ""))?.id || "") : ""));
    const [destinationUserEmail, setDestinationUserEmail] = useState("");
    const [notes, setNotes] = useState("");
    useEffect(() => {
        const nextRows = stock.filter(row => String(row.yardId) === String(sourceYardId) && Number(row.quantity) > 0);
        setItemId(current => nextRows.some(row => String(row.itemId) === String(current)) ? current : nextRows[0]?.itemId || "");
    }, [sourceYardId, stock]);
    useEffect(() => {
        if(destinationType !== "yard") return;
        const destinationYards = yards.filter(yard => String(yard.id) !== String(sourceYardId));
        setDestinationYardId(current => destinationYards.some(yard => String(yard.id) === String(current)) ? current : destinationYards[0]?.id || "");
    }, [destinationType, sourceYardId, yards]);
    const requestedQuantity = Number(quantity || 0);
    const availableQuantity = Number(selectedStock?.quantity || 0);
    const quantityExceedsStock = !!selectedStock && isPositiveQuantityText(quantity) && requestedQuantity > availableQuantity;
    const handleSubmit = async() => {
        if(!sourceYardId){ window.alert("Please select a source yard."); return; }
        if(!itemId){ window.alert("Please select an item."); return; }
        if(!quantity || !isPositiveQuantityText(quantity)){ window.alert("Please enter a quantity greater than 0."); return; }
        if(!selectedStock){ window.alert("No stock is available for the selected yard and item."); return; }
        if(quantityExceedsStock){
            window.alert(`Only ${formatQty(selectedStock.quantity)} ${selectedStock.unit || ""} available. Enter ${formatQty(selectedStock.quantity)} or less.`);
            return;
        }
        const runnerEmail = userEmailFromText(users, runnerText);
        if(!runnerText.trim()){ window.alert("Please select a runner."); return; }
        if(!runnerEmail){ window.alert("Please choose a runner from the searchable list."); return; }
        if(destinationType === "yard" && !destinationYardId){ window.alert("Please select a destination yard."); return; }
        if(destinationType === "yard" && String(destinationYardId) === String(sourceYardId)){ window.alert("Destination yard must be different from the source yard."); return; }
        if(destinationType === "user" && !destinationUserEmail){ window.alert("Please select a destination user."); return; }
        const formData = new MyFormData();
        formData.append("sourceYardId", sourceYardId);
        formData.append("itemId", itemId);
        formData.append("quantity", quantity);
        formData.append("runnerEmail", runnerEmail);
        formData.append("destinationType", destinationType);
        formData.append("destinationYardId", destinationYardId);
        formData.append("destinationUserEmail", destinationUserEmail);
        formData.append("notes", notes);
        setShowLoader(true);
        const res = await myAxios("inventory/moveStock.php", formData);
        setShowLoader(false);
        if(!res) return;
        onDone();
        closeIt();
    };
    return <Modal title = {title} closeIt = {closeIt} width = "46rem" handleFormSubmit = {handleSubmit} actionKey = "Move Inventory" formFormat = "">
        <div className = "inventoryModalBody inventoryFormGrid">
            <div>
                <label className = "font-weight-bold">Source Yard</label>
                <select className = "form-control" value = {sourceYardId} onChange = {e => setSourceYardId(e.currentTarget.value)} disabled = {!!initialStockRow}>
                    <option value = "">Select Source Yard</option>
                    {yardsWithStock.map(yard => <option key = {yard.id} value = {yard.id}>{yard.name}</option>)}
                </select>
            </div>
            <div>
                <label className = "font-weight-bold">Item</label>
                <select className = "form-control" value = {itemId} onChange = {e => setItemId(e.currentTarget.value)} disabled = {!!initialStockRow}>
                    <option value = "">Select Item</option>
                    {availableRows.map(row => <option key = {row.itemId} value = {row.itemId}>{row.itemName} — {formatQty(row.quantity)} {row.unit || "available"}</option>)}
                </select>
            </div>
            <div>
                <label className = "font-weight-bold">Quantity</label>
                <input className = {`form-control ${quantityExceedsStock ? "is-invalid" : ""}`} type = "number" min = "0.01" max = {selectedStock?.quantity || undefined} step = "any" value = {quantity} onChange = {e => setQuantity(e.currentTarget.value)} />
                {selectedStock?<small className = {`form-text ${quantityExceedsStock ? "text-danger" : "text-muted"}`}>{quantityExceedsStock ? `Only ${formatQty(selectedStock.quantity)} ${selectedStock.unit || ""} available. Enter ${formatQty(selectedStock.quantity)} or less.` : `Available: ${formatQty(selectedStock.quantity)} ${selectedStock.unit || ""}`}</small>:null}
            </div>
            <div>
                <label className = "font-weight-bold">Runner</label>
                <input className = "form-control" list = "inventoryRunnerOptions" value = {runnerText} onChange = {e => setRunnerText(e.currentTarget.value)} placeholder = "Search runner by name or email" />
                <datalist id = "inventoryRunnerOptions">
                    {users.map(user => <option key = {user.email} value = {userOptionLabel(user)} />)}
                </datalist>
            </div>
            <div>
                <label className = "font-weight-bold">Destination Type</label>
                <select className = "form-control" value = {destinationType} onChange = {e => setDestinationType(e.currentTarget.value)} disabled = {lockDestinationType}>
                    <option value = "yard">Yard</option>
                    <option value = "user">User</option>
                </select>
            </div>
            {destinationType === "yard"?
                <div>
                    <label className = "font-weight-bold">Destination Yard</label>
                    <select className = "form-control" value = {destinationYardId} onChange = {e => setDestinationYardId(e.currentTarget.value)}>
                        <option value = "">Select Destination Yard</option>
                        {yards.filter(yard => String(yard.id) !== String(sourceYardId)).map(yard => <option key = {yard.id} value = {yard.id}>{yard.name}</option>)}
                    </select>
                </div>:
                <div>
                    <label className = "font-weight-bold">Destination User</label>
                    <select className = "form-control" value = {destinationUserEmail} onChange = {e => setDestinationUserEmail(e.currentTarget.value)}>
                        <option value = "">Select Destination User</option>
                        {users.map(user => <option key = {user.email} value = {user.email}>{userLabel(user)}</option>)}
                    </select>
                </div>}
            <div className = "inventoryFormFull">
                <label className = "font-weight-bold">Notes</label>
                <textarea className = "form-control" value = {notes} onChange = {e => setNotes(e.currentTarget.value)} rows = {3} placeholder = "Optional"/>
            </div>
            {selectedStock?<div className = "inventoryFormFull inventoryMoveSummary">
                <strong>Summary:</strong> {formatQty(quantity || 0)} {itemMap[String(itemId)]?.unit || selectedStock.unit || ""} {itemMap[String(itemId)]?.name || selectedStock.itemName || "item"} from {yardMap[String(sourceYardId)]?.name || "source yard"} to {destinationType === "yard" ? (yardMap[String(destinationYardId)]?.name || "destination yard") : (users.find(user => user.email === destinationUserEmail)?.userName || "destination user")}.
            </div>:null}
        </div>
    </Modal>;
}
